'use client'
import { ChainType } from '@polaris/core/models'
import { shorten } from '@polaris/core/utils'
import { ClientOnly } from '@quirks/react'
import { useAtomValue } from 'jotai'
import { Send } from 'lucide-react'
import { WalletIcon } from '@/src/components/accounts/wallet-icon'
import { WatchAddressIcon } from '@/src/components/accounts/watch-address'
import {
  isCompleteAtom,
  routeAtom,
  toWalletInfoAtom,
  transferFundsBackStepAtom,
} from '@/src/stores/route-machine/atoms'
import { BasicStepRow, BasicStepRowTitle } from './basic-step-row'
import { PrivacyAccountLogo } from './privacy-account-logo'

const ToWalletIcon = () => {
  const route = useAtomValue(routeAtom)
  const toWallet = useAtomValue(toWalletInfoAtom)

  // Check if destination chain is Privacy Vault (Namada)
  const isPrivacyVault = route?.destinationChain.chain_type === ChainType.Namada

  if (isPrivacyVault) {
    return <PrivacyAccountLogo />
  }

  if (!toWallet?.logoUrl && !toWallet?.displayName) {
    return null
  }

  // Watch account with no logo
  if (toWallet.logoUrl === 'no-logo') {
    return (
      <ClientOnly>
        <WatchAddressIcon size='xs' className='rounded-[6px]' />
      </ClientOnly>
    )
  }

  // Destination address override
  if (toWallet.displayName === 'Destination Address') {
    return (
      <ClientOnly>
        <div className='flex items-center justify-center size-6 min-w-6 rounded-[6px] bg-orange-200'>
          <Send className='size-4 text-green-800' size={16} />
        </div>
      </ClientOnly>
    )
  }

  return (
    <ClientOnly>
      <WalletIcon
        logoUrl={toWallet.logoUrl}
        walletName={toWallet.displayName}
        size={24}
        className='max-w-none'
      />
    </ClientOnly>
  )
}

const Title = () => {
  const toWallet = useAtomValue(toWalletInfoAtom)

  return (
    <ClientOnly>
      <BasicStepRowTitle
        title={`To ${toWallet?.address ? shorten(toWallet.address, { prefixLength: 8 }) : ''}`}
      />
    </ClientOnly>
  )
}

export const ToWallet = () => {
  const toWallet = useAtomValue(toWalletInfoAtom)
  const route = useAtomValue(routeAtom)
  const transferFundsBackStep = useAtomValue(transferFundsBackStepAtom)
  const isRouteComplete = useAtomValue(isCompleteAtom)

  if (!toWallet || toWallet.address === '' || !route?.destinationChain) {
    return null
  }

  // Handle recovery states
  if (
    toWallet.isAborting !== undefined ||
    toWallet.isAbortSuccess !== undefined
  ) {
    return (
      <BasicStepRow
        title={<Title />}
        icon={<ToWalletIcon />}
        isSuccess={toWallet.isAbortSuccess}
        isActive={toWallet.isAborting}
        iconOnly
      />
    )
  }

  const isActive = transferFundsBackStep.isNeeded
    ? transferFundsBackStep.isActive
    : undefined
  const isLoading = transferFundsBackStep.isNeeded
    ? transferFundsBackStep.isLoading
    : undefined
  const isComplete = transferFundsBackStep.isSuccess || isRouteComplete

  return (
    <BasicStepRow
      title={<Title />}
      icon={<ToWalletIcon />}
      isActive={isActive}
      isSuccess={isComplete}
      isLoading={isLoading}
      iconOnly
    />
  )
}
